import type { CotacaoEnvioAr } from '@/types/arbitral/envioArTypes';
import type { CertificadoAcessosArbitral } from '@/features/arbitral-detalhes';
import { createSlice } from '@reduxjs/toolkit';

import type {
  AvaliacaoItemTriagem,
  DecisaoTriagemAgendadaView,
  DespejoEnviadoNosAjustes,
  ResultadoTriagem,
} from '@/features/secretaria-arbitral/types/triagem';

import type {
  AdvogadoDaParteItem,
  ChecklistTriagem,
  AndamentoArbitralItem,
  CalendarioAnoEstado,
  ChecklistTriagemAvaliacaoInput,
  ModeloAndamentoOpcao,
  AuditoriaTriagemEstadoProcesso,
  ArbitralAlertaItem,
  ArDetalhe,
  RequeridoAEnviarDetalhe,
  RequeridoAEnviarItem,
  ArListItem,
  ArResumo,
  CertificadoArbitralItem,
  ChatMensagem,
  DocumentoArbitralItem,
  FiltroPendencia,
  ListProcessosFilters,
  LoteExecucaoErro,
  LoteExecucaoResponse,
  LoteValidacaoResponse,
  ManifestacaoListItem,
  PainelGeralCounts,
  ParteCompletaItem,
  ListarPendenciasSecretariaResult,
  PrazoPendenciaPreview,
  PrazoEnriquecido,
  PrazosPainelContadores,
  PrazosPainelResumo,
  ProcessoArbitralListItem,
  SituacaoTriagemApi,
  ProcessosFacets,
  ProcessoDetalhe,
  RankingArbitroItem,
  ResumoTriagem,
  SorteioSugestao,
  TransitoElegibilidade,
  TriagemDecisao,
  TriagemDecisaoAtiva,
} from '@/features/secretaria-arbitral/types';
import type {
  FiltroSolicitacoes,
  SolicitacoesSecretariaResposta,
} from '@/features/secretaria-arbitral/types/solicitacoes';
import { motivoAcaoLegivel } from '@/features/secretaria-arbitral/utils/motivoLoteLabels';
import { FILTRO_PENDENCIA_PADRAO } from '@/features/secretaria-arbitral/utils/pendenciasLabels';

import {
  adicionarAdvogadoDaParte,
  desativarParteProcesso,
  reativarParteProcesso,
  reenviarLinkAcessoParte,
  removerParteProcesso,
  solicitarVerificacaoFacialParte,
  concluirSolicitacaoProcesso,
  confirmarSorteio,
  consultarDocumentoDaParte,
  criarManifestacao,
  criarParteProcesso,
  decidirSolicitacaoProcesso,
  decidirTriagem,
  editarAdvogadoDaParte,
  editarParteProcesso,
  fetchAdvogadosDaParte,
  removerAdvogadoDaParte,
  desconsiderarManifestacao,
  dispensarAlertasProcesso,
  enviarAnexoDoProcesso,
  executarLoteProcessos,
  fetchAlertasAtivos,
  fetchAndamentosProcesso,
  fetchSolicitacoesProcesso,
  fetchDiasNaoUteis,
  fetchModelosAndamentoOpcoes,
  publicarAndamentoProcesso,
  fetchAuditoriaTriagem,
  registrarExportacaoTriagem,
  cotarEnvioAr,
  fetchArAEnviar,
  fetchRequeridoAEnviar,
  fetchArDetalhe,
  fetchArDoProcesso,
  fetchArList,
  reemitirCobrancaAr,
  fetchCertificadoAcessosProcesso,
  fetchCertificadosProcesso,
  fetchChatProcesso,
  buscarNaFilaTriagem,
  fetchCasoTriagemAlvo,
  fetchDecisaoTriagemAtiva,
  fetchDetalheProcesso,
  fetchDocumentosProcesso,
  fetchElegiveisTriagem,
  fetchFilaTriagem,
  fetchListaTriagem,
  fetchResumoTriagem,
  fetchManifestacoesProcesso,
  fetchPainelGeralCounts,
  fetchPartesProcesso,
  fetchPendenciasProcesso,
  fetchPrazosAbertos,
  fetchProcessos,
  fetchProcessosFacets,
  fetchProcessosPorStatus,
  fetchRankingArbitros,
  decidirTriagemLote,
  desfazerTriagem,
  fetchChecklistTriagem,
  iniciarTriagem,
  iniciarTriagemLote,
  salvarChecklistTriagem,
  publicarDecurso,
  fetchElegibilidadeTransito,
  publicarTransito,
  liberarSentenca,
  reabrirProcesso,
  registrarConcluso,
  registrarManifestacao,
  solicitarEnvioAr,
  substituirRankingArbitros,
  sugerirSorteio,
  validarLoteProcessos,
  validarPendencia,
  criarPendenciaProcesso,
  movimentarPendencias,
  previewPrazoPendencia,
} from './actions';

const RESULTADO_POR_DECISAO_API: Record<TriagemDecisaoAtiva['tipo'], ResultadoTriagem> = {
  APROVADA: 'APROVADO',
  REGULARIZACAO: 'CORRECAO',
  NEGADA: 'NEGADO',
};

const RESULTADO_POR_TIPO_DA_LISTAGEM = new Map<string, ResultadoTriagem>(
  Object.entries(RESULTADO_POR_DECISAO_API),
);

const RESULTADO_POR_DECISAO: Record<TriagemDecisao['decisao'], ResultadoTriagem> = {
  APROVAR: 'APROVADO',
  PEDIR_CORRECAO: 'CORRECAO',
  NEGAR: 'NEGADO',
};

function avaliacoesPersistidas(
  checklist: ChecklistTriagem | null,
): Record<string, AvaliacaoItemTriagem> {
  const avaliacoes: Record<string, AvaliacaoItemTriagem> = {};
  for (const item of checklist?.itens ?? []) {
    if (item.avaliacao === 'APROVADO' || item.avaliacao === 'REPROVADO') {
      avaliacoes[item.chave] = item.avaliacao;
    }
  }
  return avaliacoes;
}

/**
 * Revisão monotônica por item avaliado — mesmo mecanismo que o slice já usa para
 * decisões agendadas (`triagemDecisaoRevisao*`), aplicado ao checklist.
 *
 * Sozinha ela não basta, e o contador de PUT em voo sozinho também não: cada um
 * cobre uma janela. A revisão pega o clique dado DEPOIS que a leitura saiu (a
 * resposta não podia conhecê-lo); o contador pega o clique dado ANTES, cujo PUT
 * ainda não commitou quando a leitura responde. As duas janelas se sobrepõem no
 * mesmo cenário — polling de 5 s com alguém avaliando itens — e a proteção é a
 * união delas.
 */
function marcarAvaliacaoLocal(
  state: SecretariaArbitralState,
  processoId: string,
  itens: ChecklistTriagemAvaliacaoInput[],
): void {
  const doProcesso = state.triagemAvaliacaoRevisaoPorItem[processoId] ?? {};
  for (const item of itens) doProcesso[item.chave] = (doProcesso[item.chave] ?? 0) + 1;
  state.triagemAvaliacaoRevisaoPorItem[processoId] = doProcesso;
}

function registrarEmVoo(
  state: SecretariaArbitralState,
  processoId: string,
  itens: ChecklistTriagemAvaliacaoInput[],
  delta: 1 | -1,
): void {
  const doProcesso = state.triagemAvaliacoesEmVoo[processoId] ?? {};
  for (const item of itens) {
    const total = (doProcesso[item.chave] ?? 0) + delta;
    if (total > 0) doProcesso[item.chave] = total;
    else delete doProcesso[item.chave];
  }
  if (Object.keys(doProcesso).length > 0) state.triagemAvaliacoesEmVoo[processoId] = doProcesso;
  else delete state.triagemAvaliacoesEmVoo[processoId];
}

interface ProtecaoDaLeitura {
  aoSair: Record<string, number>;
  agora: Record<string, number> | undefined;
  emVoo: Record<string, number> | undefined;
}

function mesclarComMudancasLocais(
  doServidor: Record<string, AvaliacaoItemTriagem>,
  local: Record<string, AvaliacaoItemTriagem> | undefined,
  { aoSair, agora, emVoo }: ProtecaoDaLeitura,
): Record<string, AvaliacaoItemTriagem> {
  const tocadasDepois = Object.entries(agora ?? {})
    .filter(([chave, revisao]) => revisao !== (aoSair[chave] ?? 0))
    .map(([chave]) => chave);
  const protegidas = new Set([...tocadasDepois, ...Object.keys(emVoo ?? {})]);
  if (protegidas.size === 0) return doServidor;
  const mesclado = { ...doServidor };
  for (const chave of protegidas) {
    const localDaChave = local?.[chave];
    if (localDaChave) mesclado[chave] = localDaChave;
    else delete mesclado[chave];
  }
  return mesclado;
}

export interface SecretariaArbitralState {
  painelGeral: {
    counts: PainelGeralCounts | null;
    loading: boolean;
    error: string | null;
  };
  porStatus: Record<
    string,
    {
      items: ProcessoArbitralListItem[];
      loading: boolean;
      error: string | null;
    }
  >;
  processos: {
    items: ProcessoArbitralListItem[];
    total: number;
    page: number;
    limit: number;
    loading: boolean;
    error: string | null;
    filters: ListProcessosFilters;
    facets: ProcessosFacets | null;
    /** Requisição em voo: com a busca digitada, resposta atrasada de um prefixo chega depois. */
    requestId: string | null;
    facetsRequestId: string | null;
  };
  /**
   * A fila do PAINEL de triagem — slot próprio, e não `processos` (web#1090).
   *
   * Antes ela dividia `processos` com a Tela 1, e o `pending` não limpa `items`:
   * navegar da Tela 1 para o painel pintava as linhas da tela anterior como cards
   * de fila, e a seleção inicial saía delas. Desde o recorte por origem isso podia
   * abrir o painel de DECISÃO sobre registro da integração externa — exatamente o
   * que a injeção de `origem` no deep-link existe para impedir, por outro caminho.
   *
   * ⚠️ O `pending` deste slot **continua não limpando `items`, de propósito**. Com o
   * slot exclusivo o que fica na tela durante o carregamento é a página ANTERIOR DA
   * PRÓPRIA FILA — que é o comportamento desejado da paginação sob demanda (#1069):
   * limpar aqui faria o painel piscar esqueleto a cada troca de página, a cada
   * decisão e a cada recarga. O defeito nunca foi manter conteúdo; era manter
   * conteúdo de OUTRA tela, e isso some por construção com a separação.
   */
  filaTriagem: {
    items: ProcessoArbitralListItem[];
    total: number;
    limit: number;
    /**
     * O cursor que PRODUZIU esta página — `null` na primeira. É a identidade da página
     * no modo keyset (não há mais `page`): é ele que o pouso da travessia compara para
     * saber se a resposta que chegou é a que ele pediu, e é ele que as recargas repetem
     * para ficar no mesmo recorte.
     */
    cursor: string | null;
    /**
     * Fronteira da página seguinte. `null` = fim da cadeia (o ÚNICO terminador);
     * `undefined` = o servidor não devolveu a chave, ou seja, core sem suporte a cursor
     * (REQ-07).
     *
     * Nasce `null`, e não `undefined`, de propósito: a ausência só pode significar
     * "servidor sem suporte" quando vem de uma RESPOSTA. Começar em `undefined` faria o
     * painel denunciar o servidor antes de falar com ele.
     */
    nextCursor: string | null | undefined;
    /** Linhas do recorte antes do primeiro item desta página — o "N" do cabeçalho. */
    anteriores: number | undefined;
    loading: boolean;
    error: string | null;
    /** Requisição em voo: descarta resposta atrasada de uma página que não é mais a pedida. */
    requestId: string | null;
  };
  listaTriagem: {
    items: ProcessoArbitralListItem[];
    total: number;
    page: number;
    limit: number;
    loading: boolean;
    error: string | null;
    filters: ListProcessosFilters;
  };
  resumoTriagem: {
    dados: ResumoTriagem | null;
    loading: boolean;
    error: string | null;
  };
  /**
   * O caso pedido no painel quando ele NÃO está na página carregada (web#1063).
   * Fica fora de `processos` de propósito: enfiá-lo na página falsearia a
   * posição na fila ("Caso N de M") e o alcance das ações em lote da página.
   */
  casoTriagemAlvo: {
    item: ProcessoArbitralListItem | null;
    /** Id OU número pedido — preservado mesmo quando `item` volta nulo (inexistente, outro tenant ou de origem externa, que a triagem não abre). */
    alvo: string | null;
    loading: boolean;
    error: string | null;
    requestId: string | null;
  };
  /** Busca da lista lateral do painel, no servidor. Slot próprio: filtrar a fila trocaria o dataset do painel. */
  buscaFilaTriagem: {
    items: ProcessoArbitralListItem[];
    /** Quantos o servidor tem para o termo — `items` é só a primeira página. */
    total: number;
    termo: string;
    loading: boolean;
    error: string | null;
    requestId: string | null;
  };
  /**
   * Conjunto INTEIRO de uma situação, para o card proativo agir sobre tudo (REQ-10).
   *
   * Sem `truncado` nem `error`: quem os consome é o RESULTADO do thunk, no
   * `.then` que abre o modal — guardá-los aqui seria um segundo dono do mesmo
   * fato, escrito e nunca lido.
   */
  elegiveisTriagem: {
    items: ProcessoArbitralListItem[];
    situacao: SituacaoTriagemApi | null;
    loading: boolean;
    requestId: string | null;
  };
  lote: {
    validando: boolean;
    validacao: LoteValidacaoResponse | null;
    erroValidacao: string | null;
    executando: boolean;
    resultado: LoteExecucaoResponse | null;
    erroExecucao: LoteExecucaoErro | null;
    validacaoRequestId: string | null;
    execucaoRequestId: string | null;
  };
  prazos: {
    items: PrazoEnriquecido[];
    resumo: PrazosPainelResumo;
    contadores: PrazosPainelContadores;
    loading: boolean;
    error: string | null;
  };
  sorteioSugestao: {
    byProcessoId: Record<string, SorteioSugestao | undefined>;
    loading: boolean;
    error: string | null;
  };
  detalheProcesso: {
    byId: Record<string, ProcessoDetalhe | undefined>;
    loadingById: Record<string, boolean | undefined>;
    errorById: Record<string, string | undefined>;
    requestIdById: Record<string, string | undefined>;
  };
  /**
   * Elegibilidade do trânsito, por processo. Vive no slice porque é estado server-fetched e
   * `requestIdById` é o que faz a resposta de uma abertura anterior do diálogo NÃO decidir
   * sobre a atual (COR-02) — mesmo desenho de `detalheProcesso`.
   */
  elegibilidadeTransito: {
    byProcessoId: Record<string, TransitoElegibilidade | undefined>;
    loadingById: Record<string, boolean | undefined>;
    errorById: Record<string, string | undefined>;
    requestIdById: Record<string, string | undefined>;
  };
  manifestacoesProcesso: {
    byProcessoId: Record<string, ManifestacaoListItem[] | undefined>;
    loading: boolean;
    error: string | null;
    /**
     * Erro das ações de manifestação (criar/registrar/desconsiderar). Separado do
     * `lastError` global porque este é escrito por dezenas de ações da feature
     * (triagem, partes, advogados, reabertura): o painel de manifestações exibia
     * erro de ação alheia disparada no shell do detalhe.
     */
    erroAcao: string | null;
  };
  pendenciasProcesso: {
    byProcessoId: Record<string, ListarPendenciasSecretariaResult | undefined>;
    /** Busca vigente por processo: resposta de `requestId` antigo é descartada. */
    requestIdPorProcesso: Record<string, string | undefined>;
    /**
     * Filtro aceso na tela, por processo. Mora aqui — e não em `useState` do painel — porque
     * quem dispara a releitura depois de uma mutação é o thunk, e o filtro capturado no
     * clique já pode estar obsoleto quando o POST volta.
     */
    filtroPorProcesso: Record<string, FiltroPendencia | undefined>;
    /**
     * Por processo, como os dados — e não global. O guard do `rejected` compara o `requestId`
     * DAQUELE processo, então a falha de um processo passa pelo guard de outro: A rejeitando
     * depois de B concluir gravava o erro de A no campo global e o painel de B trocava uma
     * lista válida por tela de erro.
     */
    loadingPorProcesso: Record<string, boolean | undefined>;
    errorPorProcesso: Record<string, string | null | undefined>;
  };
  prazoPendenciaPreview: {
    resultado: PrazoPendenciaPreview | null;
    loading: boolean;
    error: string | null;
  };
  ranking: {
    items: RankingArbitroItem[];
    loading: boolean;
    saving: boolean;
    error: string | null;
  };
  ar: {
    items: ArListItem[];
    total: number;
    page: number;
    limit: number;
    resumo: ArResumo;
    porOrigem: { arbitral: number; extrajudicial: number };
    loading: boolean;
    error: string | null;
    /**
     * Última listagem pedida. Resposta de `requestId` diferente é DESCARTADA.
     *
     * Trocar de aba rápido dispara duas buscas; se a primeira (mais lenta) chegar
     * depois da segunda, ela sobrescreve o resultado certo e a tela fica mostrando
     * a aba errada **em definitivo** — não é um flash, não se corrige sozinho.
     */
    ultimoRequestId: string | null;
    detalheById: Record<string, ArDetalhe | undefined>;
    detalheLoading: boolean;
    detalheError: string | null;
    porProcesso: Record<string, ArListItem[] | undefined>;
    /**
     * Solicitação cuja cobrança está sendo reemitida. Um id, e não um booleano:
     * a ação vive NA LINHA, e um booleano global travaria as outras linhas da
     * tabela junto com a que foi clicada.
     */
    reemitindoSolicitacaoId: string | null;
  };
  arAEnviar: {
    /** Linhas planas, paginadas pelo SERVIDOR — sem agrupamento no cliente. */
    items: RequeridoAEnviarItem[];
    /** Total do FILTRO — pagina a tabela. */
    total: number;
    /** Ignora a busca — é o número que a contagem da aba exibe. */
    totalGeral: number;
    page: number;
    limit: number;
    loading: boolean;
    error: string | null;
    solicitando: boolean;
    /** Última fila pedida; resposta de `requestId` antigo é descartada. */
    ultimoRequestId: string | null;
    detalheByParteArbitralId: Record<string, RequeridoAEnviarDetalhe | undefined>;
    detalheLoading: boolean;
    detalheError: string | null;
    cotacao: {
      resultado: CotacaoEnvioAr | null;
      loading: boolean;
      error: string | null;
      /** Distingue recusa de destino (insistir não resolve) de falha transitória. */
      errorCodigo: string | null;
      /**
       * Última cotação pedida. Respostas de `requestId` diferente são DESCARTADAS:
       * "a última chamada vence". Sem isso, digitar o CEP e corrigi-lo deixa a tela
       * mostrando o valor do CEP antigo — e a secretaria pede autorização dele.
       */
      ultimoRequestId: string | null;
    };
  };
  partesProcesso: {
    byProcessoId: Record<string, ParteCompletaItem[] | undefined>;
    loading: boolean;
    error: string | null;
  };
  advogadosParte: {
    byParteArbitralId: Record<string, AdvogadoDaParteItem[] | undefined>;
    loading: boolean;
    error: string | null;
  };
  documentosProcesso: {
    byProcessoId: Record<string, DocumentoArbitralItem[] | undefined>;
    loading: boolean;
    error: string | null;
  };
  solicitacoesProcesso: {
    byProcessoId: Record<string, SolicitacoesSecretariaResposta | undefined>;
    filtroByProcessoId: Record<string, FiltroSolicitacoes | undefined>;
    requestId: string | null;
    loading: boolean;
    error: string | null;
  };
  decisaoSolicitacao: {
    requestId: string | null;
    decidindo: boolean;
    erro: string | null;
  };
  conclusoSolicitacao: {
    requestId: string | null;
    enviando: boolean;
    erro: string | null;
  };
  andamentosProcesso: {
    byProcessoId: Record<string, AndamentoArbitralItem[] | undefined>;
    errorByProcessoId: Record<string, string | null | undefined>;
    loadingByProcessoId: Record<string, boolean | undefined>;
    requestIdByProcessoId: Record<string, string | undefined>;
  };
  publicacaoAndamento: {
    requestId: string | null;
    publicando: boolean;
    erro: string | null;
    conflito: boolean;
    andamentoId: string | null;
  };
  modelosAndamentoOpcoes: {
    items: ModeloAndamentoOpcao[];
    loading: boolean;
    error: string | null;
  };
  diasNaoUteis: {
    byAno: Record<number, CalendarioAnoEstado | undefined>;
  };
  auditoriaTriagem: {
    byProcessoId: Record<string, AuditoriaTriagemEstadoProcesso | undefined>;
  };
  exportacaoAuditoria: {
    loading: boolean;
    error: string | null;
  };
  certificadosProcesso: {
    byProcessoId: Record<string, CertificadoArbitralItem[] | undefined>;
    loading: boolean;
    error: string | null;
  };
  certificadoAcessosProcesso: {
    byProcessoId: Record<string, CertificadoAcessosArbitral | undefined>;
    loadingById: Record<string, boolean | undefined>;
    errorById: Record<string, string | undefined>;
    requestIdById: Record<string, string | undefined>;
  };
  chatProcesso: {
    byProcessoId: Record<
      string,
      { mensagens: ChatMensagem[]; total: number; page: number; limit: number } | undefined
    >;
    loading: boolean;
    error: string | null;
  };
  alertas: {
    /** Os ATIVOS dos processos da página da lista, não todos os do escopo. */
    items: ArbitralAlertaItem[];
    byProcessoId: Record<string, ArbitralAlertaItem[]>;
    /** Todos os ATIVOS do escopo, para o contador do filtro. */
    totalGeral: number;
    loading: boolean;
    error: string | null;
    requestId: string | null;
  };
  triagemChecklists: Record<string, ChecklistTriagem | undefined>;
  triagemAvaliacoes: Record<string, Record<string, AvaliacaoItemTriagem> | undefined>;
  /** Quantas vezes cada item do checklist foi avaliado localmente, por processo. */
  triagemAvaliacaoRevisaoPorItem: Record<string, Record<string, number> | undefined>;
  /** Itens com PUT ainda sem resposta, por processo — contador, não booleano. */
  triagemAvaliacoesEmVoo: Record<string, Record<string, number> | undefined>;
  /** Revisão vigente quando cada leitura do checklist saiu, por `requestId`. */
  triagemChecklistRevisaoPorRequisicao: Record<string, Record<string, number> | undefined>;
  triagemIniciadosLocais: Record<string, true | undefined>;
  triagemDecisoesAgendadas: Record<string, DecisaoTriagemAgendadaView | undefined>;
  /** Por processo: o despejo enviado nos ajustes por esta aba, para o banner de agendada (D-04). */
  triagemDespejoNosAjustes: Record<string, DespejoEnviadoNosAjustes | undefined>;
  triagemDecisaoRevisao: number;
  triagemDecisaoRevisaoPorProcesso: Record<string, number | undefined>;
  triagemListagemRevisaoPorRequisicao: Record<string, number | undefined>;
  triagemDecisaoAtiva: {
    loading: boolean;
    error: string | null;
  };
  actionLoading: {
    iniciarTriagem: boolean;
    triagem: boolean;
    triagemLote: boolean;
    desfazerTriagem: boolean;
    salvarChecklist: boolean;
    concluso: boolean;
    sorteio: boolean;
    transito: boolean;
    manifestacao: boolean;
    decurso: boolean;
    validacaoPendencia: boolean;
    criarPendenciaProcesso: boolean;
    movimentarPendencias: boolean;
    reabertura: boolean;
    liberacaoSentenca: boolean;
    consultaDocumento: boolean;
    enviarAnexo: boolean;
    salvarParte: boolean;
    salvarAdvogado: boolean;
    removerAdvogado: boolean;
    situacaoParte: boolean;
    reenviarAcesso: boolean;
    verificacaoFacial: boolean;
  };
  lastError: string | null;
}

const invalidarAuditoriaTriagem = (state: SecretariaArbitralState, processoIds: string[]) => {
  for (const processoId of processoIds) {
    delete state.auditoriaTriagem.byProcessoId[processoId];
  }
};

const marcarDecisaoConhecidaLocalmente = (
  state: SecretariaArbitralState,
  processoIds: string[],
) => {
  state.triagemDecisaoRevisao += 1;
  for (const processoId of processoIds) {
    state.triagemDecisaoRevisaoPorProcesso[processoId] = state.triagemDecisaoRevisao;
  }
};

const abrirListagemDeDecisoes = (state: SecretariaArbitralState, requestId: string) => {
  state.triagemListagemRevisaoPorRequisicao[requestId] = state.triagemDecisaoRevisao;
};

const fecharListagemDeDecisoes = (state: SecretariaArbitralState, requestId: string): number => {
  const revisao =
    state.triagemListagemRevisaoPorRequisicao[requestId] ?? state.triagemDecisaoRevisao;
  delete state.triagemListagemRevisaoPorRequisicao[requestId];
  return revisao;
};

/**
 * Guarda o despejo da decisão de ajustes que esta aba acabou de agendar; qualquer outra decisão
 * (aprovação, negativa, ou ajustes sem a chave) apaga o que havia — o banner não pode dizer o
 * despejo de uma decisão que não é a vigente.
 */
const lembrarDespejoNosAjustes = (
  state: SecretariaArbitralState,
  processoId: string,
  decisaoId: string,
  decisao: TriagemDecisao['decisao'],
  flagDespejo: boolean | undefined,
) => {
  if (decisao === 'PEDIR_CORRECAO' && typeof flagDespejo === 'boolean') {
    state.triagemDespejoNosAjustes[processoId] = { decisaoId, flagDespejo };
  } else {
    delete state.triagemDespejoNosAjustes[processoId];
  }
};

/** O servidor mostrou outra decisão (ou nenhuma): o despejo lembrado era de uma que já não vale. */
const esquecerDespejoDeOutraDecisao = (
  state: SecretariaArbitralState,
  processoId: string,
  decisaoId: string | null,
) => {
  if (state.triagemDespejoNosAjustes[processoId]?.decisaoId !== decisaoId) {
    delete state.triagemDespejoNosAjustes[processoId];
  }
};

const hidratarDecisoesAgendadas = (
  state: SecretariaArbitralState,
  items: ProcessoArbitralListItem[],
  revisaoDaListagem: number,
) => {
  for (const item of items) {
    if ((state.triagemDecisaoRevisaoPorProcesso[item.id] ?? 0) > revisaoDaListagem) continue;
    const decisao = item.decisaoAtiva;
    const resultado = decisao ? RESULTADO_POR_TIPO_DA_LISTAGEM.get(decisao.tipo) : undefined;
    if (!decisao || !resultado) continue;
    state.triagemDecisoesAgendadas[item.id] = {
      resultado,
      publicarApos: decisao.publicarApos,
      status: 'AGENDADA',
      erro: null,
    };
    esquecerDespejoDeOutraDecisao(state, item.id, decisao.decisaoId);
  }
};

const estadoAuditoriaDoProcesso = (
  state: SecretariaArbitralState,
  processoId: string,
): AuditoriaTriagemEstadoProcesso => {
  const atual = state.auditoriaTriagem.byProcessoId[processoId];
  if (atual) return atual;
  const novo: AuditoriaTriagemEstadoProcesso = { page: null, loading: false, error: null };
  state.auditoriaTriagem.byProcessoId[processoId] = novo;
  return novo;
};

const manifestacaoDoProcesso = (
  state: SecretariaArbitralState,
  referencia: { processoId: string; manifestacaoId: string },
): ManifestacaoListItem | undefined =>
  state.manifestacoesProcesso.byProcessoId[referencia.processoId]?.find(
    (item) => item.id === referencia.manifestacaoId,
  );

const LOTE_ESTADO_INICIAL: SecretariaArbitralState['lote'] = {
  validando: false,
  validacao: null,
  erroValidacao: null,
  executando: false,
  resultado: null,
  erroExecucao: null,
  validacaoRequestId: null,
  execucaoRequestId: null,
};

const initialState: SecretariaArbitralState = {
  painelGeral: { counts: null, loading: false, error: null },
  porStatus: {},
  processos: {
    items: [],
    total: 0,
    page: 1,
    limit: 10,
    loading: false,
    error: null,
    filters: {},
    facets: null,
    requestId: null,
    facetsRequestId: null,
  },
  filaTriagem: {
    items: [],
    total: 0,
    // 100 = o tamanho de página que a fila realmente pede (`FILA_TRIAGEM_LIMIT`,
    // `store/actions.ts`). Literal, e NÃO importado: `slice.ts` é alvo de `vi.mock` em 8
    // arquivos de teste que dublam `./actions` sem este export — importar daqui derruba a
    // coleta dos 8 (medido). Se um dia divergirem, o painel não quebra: antes da primeira
    // resposta `total` é 0 e a borda da travessia é decidida pelo cursor, não por conta.
    limit: 100,
    cursor: null,
    nextCursor: null,
    anteriores: 0,
    loading: false,
    error: null,
    requestId: null,
  },
  listaTriagem: {
    items: [],
    total: 0,
    page: 1,
    limit: 8,
    loading: false,
    error: null,
    filters: {},
  },
  resumoTriagem: { dados: null, loading: false, error: null },
  casoTriagemAlvo: {
    item: null,
    alvo: null,
    loading: false,
    error: null,
    requestId: null,
  },
  buscaFilaTriagem: {
    items: [],
    total: 0,
    termo: '',
    loading: false,
    error: null,
    requestId: null,
  },
  elegiveisTriagem: { items: [], situacao: null, loading: false, requestId: null },
  lote: LOTE_ESTADO_INICIAL,
  prazos: {
    items: [],
    resumo: { total: 0, vencidos: 0, venceHoje: 0 },
    contadores: { responsavel: { SECRETARIA: 0, ARBITRO: 0, PARTE: 0, SISTEMA: 0 }, tipo: {} },
    loading: true,
    error: null,
  },
  sorteioSugestao: { byProcessoId: {}, loading: false, error: null },
  detalheProcesso: { byId: {}, loadingById: {}, errorById: {}, requestIdById: {} },
  elegibilidadeTransito: { byProcessoId: {}, loadingById: {}, errorById: {}, requestIdById: {} },
  manifestacoesProcesso: { byProcessoId: {}, loading: false, error: null, erroAcao: null },
  pendenciasProcesso: {
    byProcessoId: {},
    requestIdPorProcesso: {},
    filtroPorProcesso: {},
    loadingPorProcesso: {},
    errorPorProcesso: {},
  },
  prazoPendenciaPreview: { resultado: null, loading: false, error: null },
  ranking: { items: [], loading: false, saving: false, error: null },
  ar: {
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    resumo: { total: 0, emCurso: 0, vencidos: 0, retornados: 0, aguardandoAutorizacao: 0 },
    porOrigem: { arbitral: 0, extrajudicial: 0 },
    loading: false,
    error: null,
    ultimoRequestId: null,
    detalheById: {},
    detalheLoading: false,
    detalheError: null,
    porProcesso: {},
    reemitindoSolicitacaoId: null,
  },
  arAEnviar: {
    items: [],
    total: 0,
    totalGeral: 0,
    page: 1,
    limit: 20,
    loading: false,
    error: null,
    solicitando: false,
    ultimoRequestId: null,
    detalheByParteArbitralId: {},
    detalheLoading: false,
    detalheError: null,
    cotacao: {
      resultado: null,
      loading: false,
      error: null,
      errorCodigo: null,
      ultimoRequestId: null,
    },
  },
  partesProcesso: { byProcessoId: {}, loading: false, error: null },
  advogadosParte: { byParteArbitralId: {}, loading: false, error: null },
  documentosProcesso: { byProcessoId: {}, loading: false, error: null },
  solicitacoesProcesso: {
    byProcessoId: {},
    filtroByProcessoId: {},
    requestId: null,
    loading: false,
    error: null,
  },
  decisaoSolicitacao: { requestId: null, decidindo: false, erro: null },
  conclusoSolicitacao: { requestId: null, enviando: false, erro: null },
  andamentosProcesso: {
    byProcessoId: {},
    errorByProcessoId: {},
    loadingByProcessoId: {},
    requestIdByProcessoId: {},
  },
  publicacaoAndamento: {
    requestId: null,
    publicando: false,
    erro: null,
    conflito: false,
    andamentoId: null,
  },
  modelosAndamentoOpcoes: { items: [], loading: false, error: null },
  diasNaoUteis: { byAno: {} },
  auditoriaTriagem: { byProcessoId: {} },
  exportacaoAuditoria: { loading: false, error: null },
  certificadosProcesso: { byProcessoId: {}, loading: false, error: null },
  certificadoAcessosProcesso: {
    byProcessoId: {},
    loadingById: {},
    errorById: {},
    requestIdById: {},
  },
  chatProcesso: { byProcessoId: {}, loading: false, error: null },
  alertas: {
    items: [],
    byProcessoId: {},
    totalGeral: 0,
    loading: false,
    error: null,
    requestId: null,
  },
  triagemChecklists: {},
  triagemAvaliacoes: {},
  triagemAvaliacaoRevisaoPorItem: {},
  triagemAvaliacoesEmVoo: {},
  triagemChecklistRevisaoPorRequisicao: {},
  triagemIniciadosLocais: {},
  triagemDecisoesAgendadas: {},
  triagemDespejoNosAjustes: {},
  triagemDecisaoRevisao: 0,
  triagemDecisaoRevisaoPorProcesso: {},
  triagemListagemRevisaoPorRequisicao: {},
  triagemDecisaoAtiva: { loading: false, error: null },
  actionLoading: {
    iniciarTriagem: false,
    triagem: false,
    triagemLote: false,
    desfazerTriagem: false,
    salvarChecklist: false,
    concluso: false,
    sorteio: false,
    transito: false,
    manifestacao: false,
    decurso: false,
    validacaoPendencia: false,
    criarPendenciaProcesso: false,
    movimentarPendencias: false,
    reabertura: false,
    liberacaoSentenca: false,
    consultaDocumento: false,
    enviarAnexo: false,
    salvarParte: false,
    salvarAdvogado: false,
    removerAdvogado: false,
    situacaoParte: false,
    reenviarAcesso: false,
    verificacaoFacial: false,
  },
  lastError: null,
};

const slice = createSlice({
  name: 'secretariaArbitral',
  initialState,
  reducers: {
    setFilters(state, action: { payload: ListProcessosFilters }) {
      state.processos.filters = { ...state.processos.filters, ...action.payload };
    },
    resetFilters(state) {
      state.processos.filters = {};
    },
    clearLastError(state) {
      state.lastError = null;
    },
    /**
     * Larga o conjunto de elegíveis quando o modal do card proativo fecha.
     *
     * Até 2.000 `ProcessoArbitralListItem` ficavam no Redux pelo resto da sessão
     * e continuavam entrando no `catalogo` da lista a cada recálculo. Mesmo
     * motivo (e mesma forma) do `descartarElegibilidadeTransito` logo acima —
     * limpar o `requestId` junto faz a resposta em voo ser ignorada ao chegar.
     */
    descartarElegiveisTriagem(state) {
      state.elegiveisTriagem = { items: [], situacao: null, loading: false, requestId: null };
    },
    /**
     * Adota como caso pedido um item que o cliente JÁ tem.
     *
     * A busca da lista lateral devolve o `ProcessoArbitralListItem` inteiro;
     * clicar num resultado disparava `fetchCasoTriagemAlvo` para buscar de novo o
     * mesmo dado, trocando a tela por "Abrindo o caso pedido…" — e, se essa
     * segunda chamada falhasse, dizendo "não foi possível abrir este caso" sobre
     * um caso que a tela tinha acabado de listar.
     */
    adotarCasoTriagemAlvo(state, action: { payload: ProcessoArbitralListItem }) {
      state.casoTriagemAlvo = {
        item: action.payload,
        alvo: action.payload.id,
        loading: false,
        error: null,
        requestId: null,
      };
    },
    /**
     * Larga o caso que foi aberto por id, quando o painel volta a operar sobre a
     * página carregada.
     *
     * Sem isto o caso ficava anexado a `itens` para sempre: `proximoCaso`
     * (em `TriagemPainel`) varre `itens`, então depois de decidir o último
     * caso da página a seleção pulava para um processo de outra posição da fila,
     * fora da ordem — e o marcador de "não consegui abrir" seguia apontando um
     * alvo que já tinha sido aberto. Limpar o `requestId` junto faz a resposta em
     * voo daquela busca ser ignorada quando chegar.
     */
    limparCasoTriagemAlvo(state) {
      state.casoTriagemAlvo = {
        item: null,
        alvo: null,
        loading: false,
        error: null,
        requestId: null,
      };
    },
    /**
     * Descarta a elegibilidade ao fechar o diálogo. Sem isto a reabertura decide sobre o
     * snapshot da abertura anterior enquanto o refetch corre; limpar também o `requestId` faz a
     * resposta em vôo daquela consulta ser ignorada quando chegar.
     */
    descartarElegibilidadeTransito(state, action: { payload: string }) {
      delete state.elegibilidadeTransito.byProcessoId[action.payload];
      delete state.elegibilidadeTransito.loadingById[action.payload];
      delete state.elegibilidadeTransito.errorById[action.payload];
      delete state.elegibilidadeTransito.requestIdById[action.payload];
    },
    resetPublicacaoAndamento(state) {
      if (state.publicacaoAndamento.publicando) return;
      state.publicacaoAndamento = {
        requestId: null,
        publicando: false,
        erro: null,
        conflito: false,
        andamentoId: null,
      };
    },
    resetDecisaoSolicitacao(state) {
      state.decisaoSolicitacao = { requestId: null, decidindo: false, erro: null };
    },
    resetConclusoSolicitacao(state) {
      state.conclusoSolicitacao = { requestId: null, enviando: false, erro: null };
    },
    resetLote(state) {
      state.lote = LOTE_ESTADO_INICIAL;
    },
    definirFiltroPendencias(
      state,
      action: { payload: { processoId: string; filtro: FiltroPendencia } },
    ) {
      state.pendenciasProcesso.filtroPorProcesso[action.payload.processoId] = action.payload.filtro;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchPainelGeralCounts.pending, (state) => {
        state.painelGeral.loading = true;
        state.painelGeral.error = null;
      })
      .addCase(fetchPainelGeralCounts.fulfilled, (state, action) => {
        state.painelGeral.loading = false;
        state.painelGeral.counts = action.payload;
      })
      .addCase(fetchPainelGeralCounts.rejected, (state, action) => {
        state.painelGeral.loading = false;
        state.painelGeral.error = action.error.message ?? 'Erro ao carregar painel';
      })

      .addCase(fetchProcessosPorStatus.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.porStatus[key] = { items: [], loading: true, error: null };
      })
      .addCase(fetchProcessosPorStatus.fulfilled, (state, action) => {
        state.porStatus[action.payload.key] = {
          items: action.payload.items,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchProcessosPorStatus.rejected, (state, action) => {
        const key = action.meta.arg.key;
        state.porStatus[key] = {
          items: [],
          loading: false,
          error: action.error.message ?? 'Erro ao carregar processos',
        };
      })

      // Só a última requisição escreve em `processos`: a busca dispara a cada pausa da digitação
      // e a resposta de um prefixo pode chegar depois da do termo completo (review do PR #1021,
      // COR-01).
      //
      // A fila do painel saiu daqui (web#1090): tem slot próprio, `filaTriagem`, porque o
      // `pending` não limpa `items` e o painel pintava as linhas da Tela 1 como cards de fila.
      //
      // Com isso este slot passou a ser EXCLUSIVO da Tela 1 — só `fetchProcessos` e
      // `fetchProcessosFacets` escrevem aqui. As outras superfícies da triagem já tinham slot
      // próprio antes (`listaTriagem`, `buscaFilaTriagem`, `porStatus`), então não sobrou
      // nenhum par de datasets dividindo estado. Quem acrescentar produtor aqui reabre a classe.
      .addCase(fetchProcessos.pending, (state, action) => {
        state.processos.requestId = action.meta.requestId;
        state.processos.loading = true;
        state.processos.error = null;
        state.processos.filters = action.meta.arg;
      })
      .addCase(fetchProcessos.fulfilled, (state, action) => {
        if (state.processos.requestId !== action.meta.requestId) return;
        state.processos.requestId = null;
        state.processos.loading = false;
        state.processos.items = action.payload.items;
        state.processos.total = action.payload.total;
        state.processos.page = action.payload.page;
        state.processos.limit = action.payload.limit;
      })
      .addCase(fetchProcessos.rejected, (state, action) => {
        if (state.processos.requestId !== action.meta.requestId) return;
        state.processos.requestId = null;
        state.processos.loading = false;
        state.processos.error = action.error.message ?? 'Erro ao listar processos';
      })

      .addCase(fetchProcessosFacets.pending, (state, action) => {
        state.processos.facetsRequestId = action.meta.requestId;
      })
      .addCase(fetchProcessosFacets.fulfilled, (state, action) => {
        if (state.processos.facetsRequestId !== action.meta.requestId) return;
        state.processos.facetsRequestId = null;
        state.processos.facets = action.payload;
      })
      .addCase(fetchProcessosFacets.rejected, (state, action) => {
        if (state.processos.facetsRequestId !== action.meta.requestId) return;
        state.processos.facetsRequestId = null;
        state.processos.facets = null;
      })

      .addCase(validarLoteProcessos.pending, (state, action) => {
        state.lote = { ...LOTE_ESTADO_INICIAL, validando: true };
        state.lote.validacaoRequestId = action.meta.requestId;
      })
      .addCase(validarLoteProcessos.fulfilled, (state, action) => {
        if (state.lote.validacaoRequestId !== action.meta.requestId) return;
        state.lote.validando = false;
        state.lote.validacao = action.payload;
      })
      .addCase(validarLoteProcessos.rejected, (state, action) => {
        if (state.lote.validacaoRequestId !== action.meta.requestId) return;
        state.lote.validando = false;
        state.lote.erroValidacao = action.error.message ?? 'Erro ao validar o lote';
      })

      .addCase(executarLoteProcessos.pending, (state, action) => {
        state.lote.executando = true;
        state.lote.erroExecucao = null;
        state.lote.execucaoRequestId = action.meta.requestId;
      })
      .addCase(executarLoteProcessos.fulfilled, (state, action) => {
        if (state.lote.execucaoRequestId !== action.meta.requestId) return;
        state.lote.executando = false;
        state.lote.resultado = action.payload;
      })
      .addCase(executarLoteProcessos.rejected, (state, action) => {
        if (state.lote.execucaoRequestId !== action.meta.requestId) return;
        state.lote.executando = false;
        state.lote.erroExecucao = action.payload ?? {
          mensagem: action.error.message ?? 'Erro ao executar o lote',
          conflitoIdempotencia: false,
          loteId: null,
        };
      })

      .addCase(fetchPrazosAbertos.pending, (state) => {
        state.prazos.loading = true;
        state.prazos.error = null;
      })
      .addCase(fetchPrazosAbertos.fulfilled, (state, action) => {
        state.prazos.loading = false;
        state.prazos.items = action.payload.items;
        state.prazos.resumo = action.payload.resumo;
        state.prazos.contadores = action.payload.contadores;
      })
      .addCase(fetchPrazosAbertos.rejected, (state, action) => {
        state.prazos.loading = false;
        state.prazos.error = action.error.message ?? 'Erro ao carregar prazos';
      })

      // Escreve em `filaTriagem`, NÃO em `processos` (web#1090): o slot é exclusivo
      // do painel, então a Tela 1 não vê o que a fila escreve e a fila não herda o
      // que a Tela 1 deixou. O `pending` segue sem limpar `items` de propósito —
      // ver o comentário do slot na declaração do estado.
      .addCase(fetchFilaTriagem.pending, (state, action) => {
        state.filaTriagem.requestId = action.meta.requestId;
        state.filaTriagem.loading = true;
        state.filaTriagem.error = null;
        abrirListagemDeDecisoes(state, action.meta.requestId);
      })
      .addCase(fetchFilaTriagem.fulfilled, (state, action) => {
        // Fecha a listagem antes da guarda: a chave é da própria requisição e vazaria no descarte.
        const revisao = fecharListagemDeDecisoes(state, action.meta.requestId);
        if (state.filaTriagem.requestId !== action.meta.requestId) return;
        state.filaTriagem.requestId = null;
        state.filaTriagem.loading = false;
        state.filaTriagem.items = action.payload.items;
        state.filaTriagem.total = action.payload.total;
        state.filaTriagem.limit = action.payload.limit;
        // De onde a página VEIO sai do pedido, não da resposta: o core ecoa `page`/`limit`
        // mas não repete o `cursor` recebido.
        state.filaTriagem.cursor = action.meta.arg.cursor ?? null;
        state.filaTriagem.nextCursor = action.payload.nextCursor;
        state.filaTriagem.anteriores = action.payload.anteriores;
        hidratarDecisoesAgendadas(state, action.payload.items, revisao);
      })
      .addCase(fetchFilaTriagem.rejected, (state, action) => {
        fecharListagemDeDecisoes(state, action.meta.requestId);
        if (state.filaTriagem.requestId !== action.meta.requestId) return;
        state.filaTriagem.requestId = null;
        state.filaTriagem.loading = false;
        state.filaTriagem.error = action.error.message ?? 'Erro ao carregar a fila de triagem';
      })

      .addCase(fetchListaTriagem.pending, (state, action) => {
        state.listaTriagem.loading = true;
        state.listaTriagem.error = null;
        state.listaTriagem.filters = action.meta.arg;
        abrirListagemDeDecisoes(state, action.meta.requestId);
      })
      .addCase(fetchListaTriagem.fulfilled, (state, action) => {
        state.listaTriagem.loading = false;
        state.listaTriagem.items = action.payload.items;
        state.listaTriagem.total = action.payload.total;
        state.listaTriagem.page = action.payload.page;
        state.listaTriagem.limit = action.payload.limit;
        hidratarDecisoesAgendadas(
          state,
          action.payload.items,
          fecharListagemDeDecisoes(state, action.meta.requestId),
        );
      })
      .addCase(fetchListaTriagem.rejected, (state, action) => {
        state.listaTriagem.loading = false;
        state.listaTriagem.error = action.error.message ?? 'Erro ao carregar a lista de triagem';
        fecharListagemDeDecisoes(state, action.meta.requestId);
      })

      // Token em todos os três: a busca dispara a cada pausa da digitação e o
      // clique numa linha da lista pode trocar o alvo antes de a resposta
      // anterior chegar — resposta velha não pode sobrescrever a nova (a mesma
      // disciplina de `processos`, review do PR #1021 COR-01).
      .addCase(fetchCasoTriagemAlvo.pending, (state, action) => {
        state.casoTriagemAlvo.requestId = action.meta.requestId;
        state.casoTriagemAlvo.alvo = action.meta.arg.alvo;
        state.casoTriagemAlvo.item = null;
        state.casoTriagemAlvo.loading = true;
        state.casoTriagemAlvo.error = null;
      })
      .addCase(fetchCasoTriagemAlvo.fulfilled, (state, action) => {
        if (state.casoTriagemAlvo.requestId !== action.meta.requestId) return;
        state.casoTriagemAlvo.requestId = null;
        state.casoTriagemAlvo.loading = false;
        state.casoTriagemAlvo.item = action.payload.item;
        state.casoTriagemAlvo.alvo = action.payload.alvo;
      })
      .addCase(fetchCasoTriagemAlvo.rejected, (state, action) => {
        if (state.casoTriagemAlvo.requestId !== action.meta.requestId) return;
        state.casoTriagemAlvo.requestId = null;
        state.casoTriagemAlvo.loading = false;
        state.casoTriagemAlvo.item = null;
        state.casoTriagemAlvo.error = action.error.message ?? 'Erro ao abrir o caso pedido';
      })

      // `termo` é gravado já no PENDING (e também no rejected), não só no
      // fulfilled. É ele que a tela usa para saber se o que está em `items`
      // responde ao que o operador digitou: com o termo só chegando no sucesso,
      // uma busca em voo — ou uma que falhou — deixava no slot o termo ANTERIOR,
      // e a lista afirmava "Nenhum caso encontrado" sobre uma fila de 12.631.
      .addCase(buscarNaFilaTriagem.pending, (state, action) => {
        state.buscaFilaTriagem.requestId = action.meta.requestId;
        state.buscaFilaTriagem.termo = action.meta.arg.termo;
        state.buscaFilaTriagem.loading = true;
        state.buscaFilaTriagem.error = null;
      })
      .addCase(buscarNaFilaTriagem.fulfilled, (state, action) => {
        if (state.buscaFilaTriagem.requestId !== action.meta.requestId) return;
        state.buscaFilaTriagem.requestId = null;
        state.buscaFilaTriagem.loading = false;
        state.buscaFilaTriagem.items = action.payload.items;
        state.buscaFilaTriagem.total = action.payload.total;
        state.buscaFilaTriagem.termo = action.payload.termo;
      })
      .addCase(buscarNaFilaTriagem.rejected, (state, action) => {
        if (state.buscaFilaTriagem.requestId !== action.meta.requestId) return;
        state.buscaFilaTriagem.requestId = null;
        state.buscaFilaTriagem.loading = false;
        state.buscaFilaTriagem.items = [];
        state.buscaFilaTriagem.total = 0;
        state.buscaFilaTriagem.termo = action.meta.arg.termo;
        state.buscaFilaTriagem.error = action.error.message ?? 'Erro ao buscar na fila';
      })

      // `situacao` no PENDING: os dois cards proativos dividem este slot, e sem
      // saber QUAL deles está carregando, um clique em "Iniciar triagem" acendia
      // o spinner e desabilitava também o "Avaliar triagem".
      .addCase(fetchElegiveisTriagem.pending, (state, action) => {
        state.elegiveisTriagem.requestId = action.meta.requestId;
        state.elegiveisTriagem.situacao = action.meta.arg.situacao;
        // `items` descreve a situação ANTERIOR até a resposta
        // chegar, e `TriagemLista` funde `elegiveis.items` no `catalogo` sem
        // olhar `loading`: sem limpar, o slot anuncia a situação Y carregando
        // registros de X. Num conjunto que vira decisão em lote, isso é o
        // alcance da ação mentindo sobre si mesmo.
        state.elegiveisTriagem.items = [];
        state.elegiveisTriagem.loading = true;
      })
      .addCase(fetchElegiveisTriagem.fulfilled, (state, action) => {
        if (state.elegiveisTriagem.requestId !== action.meta.requestId) return;
        state.elegiveisTriagem.requestId = null;
        state.elegiveisTriagem.loading = false;
        state.elegiveisTriagem.items = action.payload.items;
        state.elegiveisTriagem.situacao = action.payload.situacao;
      })
      .addCase(fetchElegiveisTriagem.rejected, (state, action) => {
        if (state.elegiveisTriagem.requestId !== action.meta.requestId) return;
        state.elegiveisTriagem.requestId = null;
        state.elegiveisTriagem.loading = false;
        state.elegiveisTriagem.items = [];
      })

      .addCase(fetchResumoTriagem.pending, (state) => {
        state.resumoTriagem.loading = true;
        state.resumoTriagem.error = null;
      })
      .addCase(fetchResumoTriagem.fulfilled, (state, action) => {
        state.resumoTriagem.loading = false;
        state.resumoTriagem.dados = action.payload;
      })
      .addCase(fetchResumoTriagem.rejected, (state, action) => {
        state.resumoTriagem.loading = false;
        state.resumoTriagem.error =
          action.error.message ?? 'Erro ao carregar contadores da triagem';
      })

      .addCase(iniciarTriagem.pending, (state, action) => {
        state.actionLoading.iniciarTriagem = true;
        state.lastError = null;
        state.triagemIniciadosLocais[action.meta.arg.processoId] = true;
      })
      .addCase(iniciarTriagem.fulfilled, (state, action) => {
        state.actionLoading.iniciarTriagem = false;
        invalidarAuditoriaTriagem(state, [action.payload.processoId]);
      })
      .addCase(iniciarTriagem.rejected, (state, action) => {
        state.actionLoading.iniciarTriagem = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao iniciar triagem';
        delete state.triagemIniciadosLocais[action.meta.arg.processoId];
      })

      .addCase(fetchChecklistTriagem.pending, (state, action) => {
        state.triagemChecklistRevisaoPorRequisicao[action.meta.requestId] = {
          ...(state.triagemAvaliacaoRevisaoPorItem[action.meta.arg.processoId] ?? {}),
        };
      })
      .addCase(fetchChecklistTriagem.rejected, (state, action) => {
        delete state.triagemChecklistRevisaoPorRequisicao[action.meta.requestId];
      })
      .addCase(fetchChecklistTriagem.fulfilled, (state, action) => {
        const { processoId, checklist } = action.payload;
        const aoSair = state.triagemChecklistRevisaoPorRequisicao[action.meta.requestId] ?? {};
        delete state.triagemChecklistRevisaoPorRequisicao[action.meta.requestId];
        state.triagemChecklists[processoId] = checklist;
        // A releitura corre em paralelo com o clique de avaliação (polling da
        // análise da IA, desfazer, iniciar). Item avaliado depois de esta leitura
        // sair não pode ser apagado da tela por uma resposta que não o conhece —
        // o resto do mapa continua sendo do servidor.
        state.triagemAvaliacoes[processoId] = mesclarComMudancasLocais(
          avaliacoesPersistidas(checklist),
          state.triagemAvaliacoes[processoId],
          {
            aoSair,
            agora: state.triagemAvaliacaoRevisaoPorItem[processoId],
            emVoo: state.triagemAvaliacoesEmVoo[processoId],
          },
        );
      })

      .addCase(salvarChecklistTriagem.pending, (state, action) => {
        state.actionLoading.salvarChecklist = true;
        state.lastError = null;
        const { processoId, itens } = action.meta.arg;
        const atual = state.triagemAvaliacoes[processoId] ?? {};
        for (const item of itens) {
          if (item.avaliacao === 'PENDENTE') delete atual[item.chave];
          else atual[item.chave] = item.avaliacao;
        }
        state.triagemAvaliacoes[processoId] = atual;
        marcarAvaliacaoLocal(state, processoId, itens);
        registrarEmVoo(state, processoId, itens, 1);
      })
      .addCase(salvarChecklistTriagem.fulfilled, (state, action) => {
        state.actionLoading.salvarChecklist = false;
        registrarEmVoo(state, action.meta.arg.processoId, action.meta.arg.itens, -1);
        invalidarAuditoriaTriagem(state, [action.payload.processoId]);
      })
      .addCase(salvarChecklistTriagem.rejected, (state, action) => {
        state.actionLoading.salvarChecklist = false;
        registrarEmVoo(state, action.meta.arg.processoId, action.meta.arg.itens, -1);
        // Rollback também é mudança local: sem revisão nova, a leitura em voo
        // ressuscitaria o valor que acabou de ser desfeito.
        marcarAvaliacaoLocal(state, action.meta.arg.processoId, action.meta.arg.itens);
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao salvar checklist';
        const { processoId, itens } = action.meta.arg;
        const persistidas = avaliacoesPersistidas(state.triagemChecklists[processoId] ?? null);
        const atual = state.triagemAvaliacoes[processoId] ?? {};
        for (const item of itens) {
          const anterior = persistidas[item.chave];
          if (anterior) atual[item.chave] = anterior;
          else delete atual[item.chave];
        }
        state.triagemAvaliacoes[processoId] = atual;
      })

      .addCase(fetchDecisaoTriagemAtiva.pending, (state) => {
        state.triagemDecisaoAtiva.loading = true;
        state.triagemDecisaoAtiva.error = null;
      })
      .addCase(fetchDecisaoTriagemAtiva.fulfilled, (state, action) => {
        state.triagemDecisaoAtiva.loading = false;
        const { processoId, decisao } = action.payload;
        marcarDecisaoConhecidaLocalmente(state, [processoId]);
        esquecerDespejoDeOutraDecisao(state, processoId, decisao?.decisaoId ?? null);
        if (!decisao) {
          delete state.triagemDecisoesAgendadas[processoId];
          return;
        }
        state.triagemDecisoesAgendadas[processoId] = {
          resultado: RESULTADO_POR_DECISAO_API[decisao.tipo],
          publicarApos: decisao.publicarApos,
          status: decisao.status,
          erro: decisao.erro,
        };
      })
      .addCase(fetchDecisaoTriagemAtiva.rejected, (state, action) => {
        state.triagemDecisaoAtiva.loading = false;
        state.triagemDecisaoAtiva.error =
          action.payload ?? action.error.message ?? 'Erro ao consultar decisão de triagem';
      })

      .addCase(desfazerTriagem.pending, (state) => {
        state.actionLoading.desfazerTriagem = true;
        state.lastError = null;
      })
      .addCase(desfazerTriagem.fulfilled, (state, action) => {
        state.actionLoading.desfazerTriagem = false;
        const { processoId } = action.payload;
        marcarDecisaoConhecidaLocalmente(state, [processoId]);
        delete state.triagemDecisoesAgendadas[processoId];
        delete state.triagemDespejoNosAjustes[processoId];
        delete state.triagemIniciadosLocais[processoId];
        invalidarAuditoriaTriagem(state, [action.payload.processoId]);
      })
      .addCase(desfazerTriagem.rejected, (state, action) => {
        state.actionLoading.desfazerTriagem = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao desfazer decisão';
      })

      .addCase(iniciarTriagemLote.pending, (state, action) => {
        state.actionLoading.triagemLote = true;
        state.lastError = null;
        for (const id of action.meta.arg.processoIds) state.triagemIniciadosLocais[id] = true;
      })
      .addCase(iniciarTriagemLote.fulfilled, (state, action) => {
        state.actionLoading.triagemLote = false;
        for (const falha of action.payload.falhas) {
          delete state.triagemIniciadosLocais[falha.processoArbitralId];
        }
        invalidarAuditoriaTriagem(state, action.payload.processados);
      })
      .addCase(iniciarTriagemLote.rejected, (state, action) => {
        state.actionLoading.triagemLote = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro no lote de triagem';
        for (const id of action.meta.arg.processoIds) delete state.triagemIniciadosLocais[id];
      })

      .addCase(decidirTriagemLote.pending, (state) => {
        state.actionLoading.triagemLote = true;
        state.lastError = null;
      })
      .addCase(decidirTriagemLote.fulfilled, (state, action) => {
        state.actionLoading.triagemLote = false;
        const resultado = RESULTADO_POR_DECISAO[action.meta.arg.decisao.decisao];
        marcarDecisaoConhecidaLocalmente(
          state,
          action.payload.agendadas.map((agendada) => agendada.processoArbitralId),
        );
        const { decisao } = action.meta.arg;
        for (const agendada of action.payload.agendadas) {
          state.triagemDecisoesAgendadas[agendada.processoArbitralId] = {
            resultado,
            publicarApos: agendada.publicarApos,
            status: 'AGENDADA',
            erro: null,
          };
          lembrarDespejoNosAjustes(
            state,
            agendada.processoArbitralId,
            agendada.decisaoId,
            decisao.decisao,
            decisao.flagDespejoPorProcesso?.[agendada.processoArbitralId],
          );
        }
        invalidarAuditoriaTriagem(state, action.payload.processados);
      })
      .addCase(decidirTriagemLote.rejected, (state, action) => {
        state.actionLoading.triagemLote = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro no lote de triagem';
      })

      .addCase(decidirTriagem.pending, (state) => {
        state.actionLoading.triagem = true;
        state.lastError = null;
      })
      .addCase(decidirTriagem.fulfilled, (state, action) => {
        state.actionLoading.triagem = false;
        marcarDecisaoConhecidaLocalmente(state, [action.payload.processoId]);
        state.triagemDecisoesAgendadas[action.payload.processoId] = {
          resultado: RESULTADO_POR_DECISAO[action.meta.arg.decisao.decisao],
          publicarApos: action.payload.agendada.publicarApos,
          status: 'AGENDADA',
          erro: null,
        };
        lembrarDespejoNosAjustes(
          state,
          action.payload.processoId,
          action.payload.agendada.decisaoId,
          action.meta.arg.decisao.decisao,
          action.meta.arg.decisao.flagDespejo,
        );
        invalidarAuditoriaTriagem(state, [action.payload.processoId]);
      })
      .addCase(decidirTriagem.rejected, (state, action) => {
        state.actionLoading.triagem = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao decidir triagem';
      })

      .addCase(registrarConcluso.pending, (state) => {
        state.actionLoading.concluso = true;
        state.lastError = null;
      })
      .addCase(registrarConcluso.fulfilled, (state) => {
        state.actionLoading.concluso = false;
      })
      .addCase(registrarConcluso.rejected, (state, action) => {
        state.actionLoading.concluso = false;
        state.lastError = motivoAcaoLegivel(action.error.message, 'Erro ao registrar concluso');
      })

      .addCase(sugerirSorteio.pending, (state) => {
        state.sorteioSugestao.loading = true;
        state.sorteioSugestao.error = null;
      })
      .addCase(sugerirSorteio.fulfilled, (state, action) => {
        state.sorteioSugestao.loading = false;
        state.sorteioSugestao.byProcessoId[action.payload.processoId] = action.payload.sugestao;
      })
      .addCase(sugerirSorteio.rejected, (state, action) => {
        state.sorteioSugestao.loading = false;
        state.sorteioSugestao.error = action.error.message ?? 'Erro ao sugerir sorteio';
      })

      .addCase(confirmarSorteio.pending, (state) => {
        state.actionLoading.sorteio = true;
        state.lastError = null;
      })
      .addCase(confirmarSorteio.fulfilled, (state) => {
        state.actionLoading.sorteio = false;
      })
      .addCase(confirmarSorteio.rejected, (state, action) => {
        state.actionLoading.sorteio = false;
        state.lastError = motivoAcaoLegivel(action.error.message, 'Erro ao confirmar sorteio');
      })

      .addCase(registrarManifestacao.pending, (state) => {
        state.actionLoading.manifestacao = true;
        state.lastError = null;
        state.manifestacoesProcesso.erroAcao = null;
      })
      .addCase(registrarManifestacao.fulfilled, (state, action) => {
        state.actionLoading.manifestacao = false;
        // O thunk refaz o fetch da lista antes de resolver: se esse refetch falhar,
        // o `error` fica populado apesar da ação ter dado certo (o item já foi
        // atualizado aqui embaixo). Limpar evita banner de erro sobre sucesso.
        state.manifestacoesProcesso.error = null;
        const alvo = manifestacaoDoProcesso(state, action.payload);
        if (alvo) alvo.status = 'REGISTRADA';
      })
      .addCase(registrarManifestacao.rejected, (state, action) => {
        state.actionLoading.manifestacao = false;
        const mensagem = action.payload ?? action.error.message ?? 'Erro ao registrar manifestação';
        state.lastError = mensagem;
        state.manifestacoesProcesso.erroAcao = mensagem;
      })

      .addCase(desconsiderarManifestacao.pending, (state) => {
        state.actionLoading.manifestacao = true;
        state.lastError = null;
        state.manifestacoesProcesso.erroAcao = null;
      })
      .addCase(desconsiderarManifestacao.fulfilled, (state, action) => {
        state.actionLoading.manifestacao = false;
        state.manifestacoesProcesso.error = null;
        const alvo = manifestacaoDoProcesso(state, action.payload);
        if (alvo) {
          alvo.status = 'DESCONSIDERADA';
          alvo.justificativaDesconsiderada = action.payload.justificativa;
        }
      })
      .addCase(desconsiderarManifestacao.rejected, (state, action) => {
        state.actionLoading.manifestacao = false;
        const mensagem =
          action.payload ?? action.error.message ?? 'Erro ao desconsiderar manifestação';
        state.lastError = mensagem;
        state.manifestacoesProcesso.erroAcao = mensagem;
      })

      .addCase(criarManifestacao.pending, (state) => {
        state.actionLoading.manifestacao = true;
        state.lastError = null;
        state.manifestacoesProcesso.erroAcao = null;
      })
      .addCase(criarManifestacao.fulfilled, (state, action) => {
        state.actionLoading.manifestacao = false;
        state.manifestacoesProcesso.error = null;
        const { processoId, manifestacao } = action.payload;
        const atuais = state.manifestacoesProcesso.byProcessoId[processoId] ?? [];
        state.manifestacoesProcesso.byProcessoId[processoId] = [
          manifestacao,
          ...atuais.filter((item) => item.id !== manifestacao.id),
        ];
      })
      .addCase(criarManifestacao.rejected, (state, action) => {
        state.actionLoading.manifestacao = false;
        const mensagem =
          action.payload ?? action.error.message ?? 'Erro ao registrar a manifestação';
        state.lastError = mensagem;
        state.manifestacoesProcesso.erroAcao = mensagem;
      })

      .addCase(fetchElegibilidadeTransito.pending, (state, action) => {
        state.elegibilidadeTransito.loadingById[action.meta.arg] = true;
        state.elegibilidadeTransito.requestIdById[action.meta.arg] = action.meta.requestId;
        delete state.elegibilidadeTransito.errorById[action.meta.arg];
        delete state.elegibilidadeTransito.byProcessoId[action.meta.arg];
      })
      .addCase(fetchElegibilidadeTransito.fulfilled, (state, action) => {
        const { processoId } = action.payload;
        if (state.elegibilidadeTransito.requestIdById[processoId] !== action.meta.requestId) return;
        delete state.elegibilidadeTransito.loadingById[processoId];
        state.elegibilidadeTransito.byProcessoId[processoId] = action.payload.elegibilidade;
      })
      .addCase(fetchElegibilidadeTransito.rejected, (state, action) => {
        const processoId = action.meta.arg;
        if (state.elegibilidadeTransito.requestIdById[processoId] !== action.meta.requestId) return;
        delete state.elegibilidadeTransito.loadingById[processoId];
        // A mensagem é a do `rejectWithValue`, e não a serializada: é ela que distingue o 404 de
        // "back ainda não deployado" de um 403 ou 500 (COR-10).
        state.elegibilidadeTransito.errorById[processoId] =
          action.payload ?? action.error.message ?? 'Erro ao verificar a elegibilidade';
      })

      .addCase(publicarTransito.pending, (state) => {
        state.actionLoading.transito = true;
        state.lastError = null;
      })
      .addCase(publicarTransito.fulfilled, (state) => {
        state.actionLoading.transito = false;
      })
      .addCase(publicarTransito.rejected, (state, action) => {
        state.actionLoading.transito = false;
        state.lastError = motivoAcaoLegivel(action.error.message, 'Erro ao publicar trânsito');
      })

      .addCase(fetchDetalheProcesso.pending, (state, action) => {
        state.detalheProcesso.loadingById[action.meta.arg] = true;
        state.detalheProcesso.requestIdById[action.meta.arg] = action.meta.requestId;
        delete state.detalheProcesso.errorById[action.meta.arg];
      })
      .addCase(fetchDetalheProcesso.fulfilled, (state, action) => {
        if (state.detalheProcesso.requestIdById[action.meta.arg] !== action.meta.requestId) return;
        delete state.detalheProcesso.loadingById[action.meta.arg];
        state.detalheProcesso.byId[action.payload.id] = action.payload;
        delete state.detalheProcesso.errorById[action.meta.arg];
      })
      .addCase(fetchDetalheProcesso.rejected, (state, action) => {
        if (state.detalheProcesso.requestIdById[action.meta.arg] !== action.meta.requestId) return;
        delete state.detalheProcesso.loadingById[action.meta.arg];
        state.detalheProcesso.errorById[action.meta.arg] =
          action.error.message ?? 'Erro ao carregar detalhe';
      })

      .addCase(reabrirProcesso.pending, (state) => {
        state.actionLoading.reabertura = true;
        state.lastError = null;
      })
      .addCase(reabrirProcesso.fulfilled, (state, action) => {
        state.actionLoading.reabertura = false;
        const detalhe = state.detalheProcesso.byId[action.payload.processoArbitralId];
        if (detalhe) detalhe.statusArbitral = action.payload.novoStatus;
      })
      .addCase(reabrirProcesso.rejected, (state, action) => {
        state.actionLoading.reabertura = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao reabrir processo';
      })

      .addCase(liberarSentenca.pending, (state) => {
        state.actionLoading.liberacaoSentenca = true;
        state.lastError = null;
      })
      .addCase(liberarSentenca.fulfilled, (state) => {
        state.actionLoading.liberacaoSentenca = false;
      })
      .addCase(liberarSentenca.rejected, (state, action) => {
        state.actionLoading.liberacaoSentenca = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao liberar a sentença';
      })

      .addCase(fetchManifestacoesProcesso.pending, (state) => {
        state.manifestacoesProcesso.loading = true;
        state.manifestacoesProcesso.error = null;
      })
      .addCase(fetchManifestacoesProcesso.fulfilled, (state, action) => {
        state.manifestacoesProcesso.loading = false;
        state.manifestacoesProcesso.byProcessoId[action.payload.processoId] = action.payload.items;
      })
      .addCase(fetchManifestacoesProcesso.rejected, (state, action) => {
        state.manifestacoesProcesso.loading = false;
        state.manifestacoesProcesso.error =
          action.error.message ?? 'Erro ao carregar manifestações';
      })

      // Trocar de filtro dispara buscas concorrentes do MESMO processo. Sem descartar a
      // resposta obsoleta, a que chega por último vence e a lista passa a contradizer o filtro
      // aceso. `requestId` do RTK identifica qual busca ainda é a vigente.
      .addCase(fetchPendenciasProcesso.pending, (state, action) => {
        const proc = action.meta.arg.processoId;
        state.pendenciasProcesso.requestIdPorProcesso[proc] = action.meta.requestId;
        state.pendenciasProcesso.loadingPorProcesso[proc] = true;
        state.pendenciasProcesso.errorPorProcesso[proc] = null;
      })
      // Defesa em profundidade sobre o `requestId`: a releitura que sai DEPOIS de uma mutação
      // registra o requestId mais novo e passaria no teste acima mesmo carregando um filtro que
      // a tela já abandonou — lista de um filtro sob o chip de outro.
      .addCase(fetchPendenciasProcesso.fulfilled, (state, action) => {
        const vigenteF = state.pendenciasProcesso.requestIdPorProcesso[action.payload.processoId];
        if (vigenteF !== undefined && vigenteF !== action.meta.requestId) return;
        const filtroVigente =
          state.pendenciasProcesso.filtroPorProcesso[action.payload.processoId] ??
          FILTRO_PENDENCIA_PADRAO;
        if ((action.meta.arg.filtro ?? FILTRO_PENDENCIA_PADRAO) !== filtroVigente) return;
        state.pendenciasProcesso.loadingPorProcesso[action.payload.processoId] = false;
        state.pendenciasProcesso.byProcessoId[action.payload.processoId] = action.payload.resultado;
      })
      .addCase(fetchPendenciasProcesso.rejected, (state, action) => {
        const vigenteR = state.pendenciasProcesso.requestIdPorProcesso[action.meta.arg.processoId];
        if (vigenteR !== undefined && vigenteR !== action.meta.requestId) return;
        state.pendenciasProcesso.loadingPorProcesso[action.meta.arg.processoId] = false;
        state.pendenciasProcesso.errorPorProcesso[action.meta.arg.processoId] =
          action.error.message ?? 'Erro ao carregar pendências';
      })

      // Erro de AÇÃO vai para `lastError`, nunca para `pendenciasProcesso.error`: aquele campo
      // significa "a lista não carregou" e o painel troca a aba inteira por uma tela de erro sem
      // retomada quando ele é truthy. O 409 de "já foi movimentada por outra pessoa" é esperado
      // e já vira toast — não pode apagar lista, filtros e composer preenchido junto.
      .addCase(criarPendenciaProcesso.pending, (state) => {
        state.actionLoading.criarPendenciaProcesso = true;
        state.lastError = null;
      })
      .addCase(criarPendenciaProcesso.fulfilled, (state) => {
        state.actionLoading.criarPendenciaProcesso = false;
      })
      .addCase(criarPendenciaProcesso.rejected, (state, action) => {
        state.actionLoading.criarPendenciaProcesso = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao criar a pendência';
      })

      .addCase(movimentarPendencias.pending, (state) => {
        state.actionLoading.movimentarPendencias = true;
        state.lastError = null;
      })
      .addCase(movimentarPendencias.fulfilled, (state) => {
        state.actionLoading.movimentarPendencias = false;
      })
      .addCase(movimentarPendencias.rejected, (state, action) => {
        state.actionLoading.movimentarPendencias = false;
        state.lastError =
          action.payload ?? action.error.message ?? 'Erro ao movimentar as pendências';
      })

      .addCase(previewPrazoPendencia.pending, (state) => {
        state.prazoPendenciaPreview.loading = true;
        state.prazoPendenciaPreview.error = null;
      })
      .addCase(previewPrazoPendencia.fulfilled, (state, action) => {
        state.prazoPendenciaPreview.loading = false;
        state.prazoPendenciaPreview.resultado = action.payload;
      })
      .addCase(previewPrazoPendencia.rejected, (state, action) => {
        state.prazoPendenciaPreview.loading = false;
        state.prazoPendenciaPreview.resultado = null;
        state.prazoPendenciaPreview.error =
          action.payload ?? action.error.message ?? 'Erro ao calcular o prazo';
      })

      .addCase(validarPendencia.pending, (state) => {
        state.actionLoading.validacaoPendencia = true;
        state.lastError = null;
      })
      .addCase(validarPendencia.fulfilled, (state) => {
        state.actionLoading.validacaoPendencia = false;
      })
      .addCase(validarPendencia.rejected, (state, action) => {
        state.actionLoading.validacaoPendencia = false;
        state.lastError = motivoAcaoLegivel(action.error.message, 'Erro ao validar pendência');
      })

      .addCase(publicarDecurso.pending, (state) => {
        state.actionLoading.decurso = true;
        state.lastError = null;
      })
      .addCase(publicarDecurso.fulfilled, (state) => {
        state.actionLoading.decurso = false;
      })
      .addCase(publicarDecurso.rejected, (state, action) => {
        state.actionLoading.decurso = false;
        state.lastError = motivoAcaoLegivel(action.error.message, 'Erro ao publicar decurso');
      })

      .addCase(fetchRankingArbitros.pending, (state) => {
        state.ranking.loading = true;
        state.ranking.error = null;
      })
      .addCase(fetchRankingArbitros.fulfilled, (state, action) => {
        state.ranking.loading = false;
        state.ranking.items = action.payload;
      })
      .addCase(fetchRankingArbitros.rejected, (state, action) => {
        state.ranking.loading = false;
        state.ranking.error = action.error.message ?? 'Erro ao carregar ranking';
      })

      .addCase(substituirRankingArbitros.pending, (state) => {
        state.ranking.saving = true;
        state.ranking.error = null;
      })
      .addCase(substituirRankingArbitros.fulfilled, (state, action) => {
        state.ranking.saving = false;
        state.ranking.items = action.payload;
      })
      .addCase(substituirRankingArbitros.rejected, (state, action) => {
        state.ranking.saving = false;
        state.ranking.error = action.error.message ?? 'Erro ao salvar ranking';
      })

      .addCase(fetchArList.pending, (state, action) => {
        state.ar.loading = true;
        state.ar.error = null;
        state.ar.ultimoRequestId = action.meta.requestId;
        // As linhas em tela pertencem à consulta ANTERIOR — outra aba, outro
        // filtro. Mantê-las enquanto a nova carrega faz a tabela afirmar, sob o
        // cabeçalho novo, um resultado que não é dele. Limpar aqui também é o que
        // acende o esqueleto, porque a tela condiciona o loading a `items` vazio.
        // `resumo`/`porOrigem` ficam: são contadores do tenant e não piscam.
        state.ar.items = [];
      })
      .addCase(fetchArList.fulfilled, (state, action) => {
        // "Última chamada vence": resposta de uma consulta já superada é
        // descartada, senão a aba lenta sobrescreve a rápida e o erro fica de pé.
        if (state.ar.ultimoRequestId !== action.meta.requestId) return;
        state.ar.loading = false;
        state.ar.items = action.payload.items;
        state.ar.total = action.payload.total;
        state.ar.page = action.payload.page;
        state.ar.limit = action.payload.limit;
        state.ar.resumo = action.payload.resumo;
        state.ar.porOrigem = action.payload.porOrigem;
      })
      .addCase(fetchArList.rejected, (state, action) => {
        if (state.ar.ultimoRequestId !== action.meta.requestId) return;
        state.ar.loading = false;
        state.ar.error = action.error.message ?? 'Erro ao listar AR';
      })

      .addCase(reemitirCobrancaAr.pending, (state, action) => {
        state.ar.reemitindoSolicitacaoId = action.meta.arg;
      })
      .addCase(reemitirCobrancaAr.fulfilled, (state, action) => {
        state.ar.reemitindoSolicitacaoId = null;
        // Só grava o que o servidor AFIRMOU. Sem `situacaoCobranca` na resposta, a
        // linha fica como está e quem corrige é a listagem recarregada — pintar
        // "em aberto" por otimismo esconderia uma emissão que falhou de novo.
        if (!action.payload.situacaoCobranca) return;
        const linha = state.ar.items.find(
          (ar) => ar.solicitacaoId === action.payload.solicitacaoId,
        );
        if (linha) linha.situacaoCobranca = action.payload.situacaoCobranca;
      })
      .addCase(reemitirCobrancaAr.rejected, (state) => {
        state.ar.reemitindoSolicitacaoId = null;
      })

      .addCase(fetchArAEnviar.pending, (state, action) => {
        state.arAEnviar.loading = true;
        state.arAEnviar.error = null;
        state.arAEnviar.ultimoRequestId = action.meta.requestId;
        // Mesma régua da lista: busca nova não pode renderizar o recorte antigo.
        // `totalGeral` NÃO é limpo — é a contagem da aba, e zerá-la faria o número
        // piscar 0 a cada busca, que é o defeito que acabamos de corrigir.
        state.arAEnviar.items = [];
      })
      .addCase(fetchArAEnviar.fulfilled, (state, action) => {
        if (state.arAEnviar.ultimoRequestId !== action.meta.requestId) return;
        state.arAEnviar.loading = false;
        state.arAEnviar.items = action.payload.items;
        state.arAEnviar.total = action.payload.total;
        state.arAEnviar.totalGeral = action.payload.totalGeral;
        state.arAEnviar.page = action.payload.page;
        state.arAEnviar.limit = action.payload.limit;
      })
      .addCase(fetchArAEnviar.rejected, (state, action) => {
        if (state.arAEnviar.ultimoRequestId !== action.meta.requestId) return;
        state.arAEnviar.loading = false;
        // `items` já foi limpo no `pending` — o que importa preservar é o
        // `totalGeral`, e ele não é tocado aqui. Sem isso, falha de rede zeraria a
        // contagem da aba e a tela diria "nenhum AR a enviar", que é a mensagem
        // OPOSTA: fila vazia aqui significa "a citação digital está dando conta".
        // O estado de erro da tabela é quem assume, com "Tentar novamente".
        state.arAEnviar.error = action.error.message ?? 'Erro ao carregar a fila de AR a enviar';
      })

      .addCase(fetchRequeridoAEnviar.pending, (state) => {
        state.arAEnviar.detalheLoading = true;
        state.arAEnviar.detalheError = null;
      })
      .addCase(fetchRequeridoAEnviar.fulfilled, (state, action) => {
        state.arAEnviar.detalheLoading = false;
        state.arAEnviar.detalheByParteArbitralId[action.payload.parteArbitralId] = action.payload;
      })
      .addCase(fetchRequeridoAEnviar.rejected, (state, action) => {
        state.arAEnviar.detalheLoading = false;
        state.arAEnviar.detalheError = action.error.message ?? 'Erro ao carregar o requerido';
      })

      .addCase(cotarEnvioAr.pending, (state, action) => {
        state.arAEnviar.cotacao.loading = true;
        state.arAEnviar.cotacao.error = null;
        state.arAEnviar.cotacao.errorCodigo = null;
        state.arAEnviar.cotacao.ultimoRequestId = action.meta.requestId;
      })
      .addCase(cotarEnvioAr.fulfilled, (state, action) => {
        // "Última chamada vence": resposta de uma cotação que já foi superada é
        // descartada. Determinístico e sem AbortController — o servidor não
        // cancela a transportadora de qualquer forma.
        if (state.arAEnviar.cotacao.ultimoRequestId !== action.meta.requestId) return;
        state.arAEnviar.cotacao.loading = false;
        state.arAEnviar.cotacao.resultado = action.payload;
      })
      .addCase(cotarEnvioAr.rejected, (state, action) => {
        if (state.arAEnviar.cotacao.ultimoRequestId !== action.meta.requestId) return;
        state.arAEnviar.cotacao.loading = false;
        // A causa vem no `payload` do `rejectWithValue` — o `error` serializado
        // perderia o código, e é ele que decide se a tela manda tentar de novo.
        state.arAEnviar.cotacao.error =
          action.payload?.mensagem ??
          action.error.message ??
          'Não foi possível calcular o valor do envio';
        state.arAEnviar.cotacao.errorCodigo = action.payload?.codigo ?? null;
      })

      .addCase(solicitarEnvioAr.pending, (state) => {
        state.arAEnviar.solicitando = true;
      })
      .addCase(solicitarEnvioAr.fulfilled, (state, action) => {
        state.arAEnviar.solicitando = false;
        const antes = state.arAEnviar.items.length;
        state.arAEnviar.items = state.arAEnviar.items.filter(
          (item) => item.parteArbitralId !== action.payload.parteArbitralId,
        );
        // Decrementa só se a linha estava mesmo nesta página: os totais são do
        // servidor, e subtrair às cegas os dessincronizaria num replay.
        // Os DOIS caem: o requerido saiu da fila de fato, não só do recorte.
        if (state.arAEnviar.items.length < antes) {
          state.arAEnviar.total = Math.max(0, state.arAEnviar.total - 1);
          state.arAEnviar.totalGeral = Math.max(0, state.arAEnviar.totalGeral - 1);
        }
      })
      .addCase(solicitarEnvioAr.rejected, (state) => {
        state.arAEnviar.solicitando = false;
      })

      .addCase(fetchArDetalhe.pending, (state) => {
        state.ar.detalheLoading = true;
        state.ar.detalheError = null;
      })
      .addCase(fetchArDetalhe.fulfilled, (state, action) => {
        state.ar.detalheLoading = false;
        state.ar.detalheById[action.payload.id] = action.payload;
      })
      .addCase(fetchArDetalhe.rejected, (state, action) => {
        state.ar.detalheLoading = false;
        state.ar.detalheError = action.error.message ?? 'Erro ao carregar detalhe AR';
      })

      .addCase(fetchArDoProcesso.fulfilled, (state, action) => {
        state.ar.porProcesso[action.payload.processoId] = action.payload.items;
      })

      .addCase(fetchPartesProcesso.pending, (state) => {
        state.partesProcesso.loading = true;
        state.partesProcesso.error = null;
      })
      .addCase(fetchPartesProcesso.fulfilled, (state, action) => {
        state.partesProcesso.loading = false;
        state.partesProcesso.byProcessoId[action.payload.processoId] = action.payload.items;
      })
      .addCase(fetchPartesProcesso.rejected, (state, action) => {
        state.partesProcesso.loading = false;
        state.partesProcesso.error = action.error.message ?? 'Erro ao carregar partes';
      })

      .addCase(consultarDocumentoDaParte.pending, (state) => {
        state.actionLoading.consultaDocumento = true;
      })
      .addCase(consultarDocumentoDaParte.fulfilled, (state) => {
        state.actionLoading.consultaDocumento = false;
      })
      .addCase(consultarDocumentoDaParte.rejected, (state) => {
        state.actionLoading.consultaDocumento = false;
      })

      .addCase(enviarAnexoDoProcesso.pending, (state) => {
        state.actionLoading.enviarAnexo = true;
      })
      .addCase(enviarAnexoDoProcesso.fulfilled, (state) => {
        state.actionLoading.enviarAnexo = false;
      })
      .addCase(enviarAnexoDoProcesso.rejected, (state) => {
        state.actionLoading.enviarAnexo = false;
      })

      .addCase(criarParteProcesso.pending, (state) => {
        state.actionLoading.salvarParte = true;
        state.lastError = null;
      })
      .addCase(criarParteProcesso.fulfilled, (state) => {
        state.actionLoading.salvarParte = false;
      })
      .addCase(criarParteProcesso.rejected, (state, action) => {
        state.actionLoading.salvarParte = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao cadastrar parte';
      })

      .addCase(editarParteProcesso.pending, (state) => {
        state.actionLoading.salvarParte = true;
        state.lastError = null;
      })
      .addCase(editarParteProcesso.fulfilled, (state) => {
        state.actionLoading.salvarParte = false;
      })
      .addCase(editarParteProcesso.rejected, (state, action) => {
        state.actionLoading.salvarParte = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao editar parte';
      })

      .addCase(fetchAdvogadosDaParte.pending, (state) => {
        state.advogadosParte.loading = true;
        state.advogadosParte.error = null;
      })
      .addCase(fetchAdvogadosDaParte.fulfilled, (state, action) => {
        state.advogadosParte.loading = false;
        state.advogadosParte.byParteArbitralId[action.payload.parteArbitralId] =
          action.payload.items;
      })
      .addCase(fetchAdvogadosDaParte.rejected, (state, action) => {
        state.advogadosParte.loading = false;
        state.advogadosParte.error =
          action.payload ?? action.error.message ?? 'Erro ao carregar advogados da parte';
      })

      .addCase(adicionarAdvogadoDaParte.pending, (state) => {
        state.actionLoading.salvarAdvogado = true;
        state.lastError = null;
      })
      .addCase(adicionarAdvogadoDaParte.fulfilled, (state) => {
        state.actionLoading.salvarAdvogado = false;
      })
      .addCase(adicionarAdvogadoDaParte.rejected, (state, action) => {
        state.actionLoading.salvarAdvogado = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao constituir advogado';
      })

      .addCase(editarAdvogadoDaParte.pending, (state) => {
        state.actionLoading.salvarAdvogado = true;
        state.lastError = null;
      })
      .addCase(editarAdvogadoDaParte.fulfilled, (state) => {
        state.actionLoading.salvarAdvogado = false;
      })
      .addCase(editarAdvogadoDaParte.rejected, (state, action) => {
        state.actionLoading.salvarAdvogado = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao editar advogado';
      })

      .addCase(removerAdvogadoDaParte.pending, (state) => {
        state.actionLoading.removerAdvogado = true;
        state.lastError = null;
      })
      .addCase(removerAdvogadoDaParte.fulfilled, (state) => {
        state.actionLoading.removerAdvogado = false;
      })
      .addCase(removerAdvogadoDaParte.rejected, (state, action) => {
        state.actionLoading.removerAdvogado = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao remover advogado';
      })

      .addCase(desativarParteProcesso.pending, (state) => {
        state.actionLoading.situacaoParte = true;
        state.lastError = null;
      })
      .addCase(desativarParteProcesso.fulfilled, (state) => {
        state.actionLoading.situacaoParte = false;
      })
      .addCase(desativarParteProcesso.rejected, (state, action) => {
        state.actionLoading.situacaoParte = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao desativar parte';
      })

      .addCase(reativarParteProcesso.pending, (state) => {
        state.actionLoading.situacaoParte = true;
        state.lastError = null;
      })
      .addCase(reativarParteProcesso.fulfilled, (state) => {
        state.actionLoading.situacaoParte = false;
      })
      .addCase(reativarParteProcesso.rejected, (state, action) => {
        state.actionLoading.situacaoParte = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao reativar parte';
      })

      .addCase(removerParteProcesso.pending, (state) => {
        state.actionLoading.situacaoParte = true;
        state.lastError = null;
      })
      .addCase(removerParteProcesso.fulfilled, (state) => {
        state.actionLoading.situacaoParte = false;
      })
      .addCase(removerParteProcesso.rejected, (state, action) => {
        state.actionLoading.situacaoParte = false;
        state.lastError = action.payload ?? action.error.message ?? 'Erro ao remover parte';
      })

      .addCase(reenviarLinkAcessoParte.pending, (state) => {
        state.actionLoading.reenviarAcesso = true;
        state.lastError = null;
      })
      .addCase(reenviarLinkAcessoParte.fulfilled, (state) => {
        state.actionLoading.reenviarAcesso = false;
      })
      .addCase(reenviarLinkAcessoParte.rejected, (state, action) => {
        state.actionLoading.reenviarAcesso = false;
        state.lastError =
          action.payload ?? action.error.message ?? 'Erro ao reenviar o link de acesso';
      })

      .addCase(solicitarVerificacaoFacialParte.pending, (state) => {
        state.actionLoading.verificacaoFacial = true;
        state.lastError = null;
      })
      .addCase(solicitarVerificacaoFacialParte.fulfilled, (state) => {
        state.actionLoading.verificacaoFacial = false;
      })
      .addCase(solicitarVerificacaoFacialParte.rejected, (state, action) => {
        state.actionLoading.verificacaoFacial = false;
        state.lastError =
          action.payload ?? action.error.message ?? 'Erro ao enviar a verificação facial';
      })

      .addCase(fetchDocumentosProcesso.pending, (state) => {
        state.documentosProcesso.loading = true;
        state.documentosProcesso.error = null;
      })
      .addCase(fetchDocumentosProcesso.fulfilled, (state, action) => {
        state.documentosProcesso.loading = false;
        state.documentosProcesso.byProcessoId[action.payload.processoId] = action.payload.items;
      })
      .addCase(fetchDocumentosProcesso.rejected, (state, action) => {
        state.documentosProcesso.loading = false;
        state.documentosProcesso.error = action.error.message ?? 'Erro ao carregar documentos';
      })

      .addCase(fetchSolicitacoesProcesso.pending, (state, action) => {
        state.solicitacoesProcesso.requestId = action.meta.requestId;
        state.solicitacoesProcesso.loading = true;
        state.solicitacoesProcesso.error = null;
      })
      .addCase(fetchSolicitacoesProcesso.fulfilled, (state, action) => {
        if (state.solicitacoesProcesso.requestId !== action.meta.requestId) return;
        state.solicitacoesProcesso.requestId = null;
        state.solicitacoesProcesso.loading = false;
        state.solicitacoesProcesso.byProcessoId[action.payload.processoId] =
          action.payload.resposta;
        state.solicitacoesProcesso.filtroByProcessoId[action.payload.processoId] =
          action.payload.filtro;
      })
      .addCase(fetchSolicitacoesProcesso.rejected, (state, action) => {
        if (state.solicitacoesProcesso.requestId !== action.meta.requestId) return;
        state.solicitacoesProcesso.requestId = null;
        state.solicitacoesProcesso.loading = false;
        state.solicitacoesProcesso.error = action.error.message ?? 'Erro ao carregar solicitações';
      })

      .addCase(decidirSolicitacaoProcesso.pending, (state, action) => {
        state.decisaoSolicitacao = {
          requestId: action.meta.requestId,
          decidindo: true,
          erro: null,
        };
      })
      .addCase(decidirSolicitacaoProcesso.fulfilled, (state, action) => {
        if (state.decisaoSolicitacao.requestId !== action.meta.requestId) return;
        state.decisaoSolicitacao = { requestId: null, decidindo: false, erro: null };
      })
      .addCase(decidirSolicitacaoProcesso.rejected, (state, action) => {
        if (state.decisaoSolicitacao.requestId !== action.meta.requestId) return;
        state.decisaoSolicitacao = {
          requestId: null,
          decidindo: false,
          erro: action.payload ?? action.error.message ?? 'Erro ao registrar a decisão',
        };
      })

      .addCase(concluirSolicitacaoProcesso.pending, (state, action) => {
        state.conclusoSolicitacao = {
          requestId: action.meta.requestId,
          enviando: true,
          erro: null,
        };
      })
      .addCase(concluirSolicitacaoProcesso.fulfilled, (state, action) => {
        if (state.conclusoSolicitacao.requestId !== action.meta.requestId) return;
        state.conclusoSolicitacao = { requestId: null, enviando: false, erro: null };
      })
      .addCase(concluirSolicitacaoProcesso.rejected, (state, action) => {
        if (state.conclusoSolicitacao.requestId !== action.meta.requestId) return;
        state.conclusoSolicitacao = {
          requestId: null,
          enviando: false,
          erro: action.payload ?? action.error.message ?? 'Erro ao concluir ao árbitro',
        };
      })

      .addCase(fetchAndamentosProcesso.pending, (state, action) => {
        state.andamentosProcesso.requestIdByProcessoId[action.meta.arg] = action.meta.requestId;
        state.andamentosProcesso.loadingByProcessoId[action.meta.arg] = true;
        state.andamentosProcesso.errorByProcessoId[action.meta.arg] = null;
      })
      .addCase(fetchAndamentosProcesso.fulfilled, (state, action) => {
        const { processoId } = action.payload;
        if (state.andamentosProcesso.requestIdByProcessoId[processoId] !== action.meta.requestId) {
          return;
        }
        state.andamentosProcesso.loadingByProcessoId[processoId] = false;
        state.andamentosProcesso.byProcessoId[processoId] = action.payload.items;
      })
      .addCase(fetchAndamentosProcesso.rejected, (state, action) => {
        const processoId = action.meta.arg;
        if (state.andamentosProcesso.requestIdByProcessoId[processoId] !== action.meta.requestId) {
          return;
        }
        state.andamentosProcesso.loadingByProcessoId[processoId] = false;
        state.andamentosProcesso.errorByProcessoId[processoId] =
          action.error.message ?? 'Erro ao carregar andamentos';
      })

      .addCase(publicarAndamentoProcesso.pending, (state, action) => {
        state.publicacaoAndamento = {
          requestId: action.meta.requestId,
          publicando: true,
          erro: null,
          conflito: false,
          andamentoId: null,
        };
      })
      .addCase(publicarAndamentoProcesso.fulfilled, (state, action) => {
        if (state.publicacaoAndamento.requestId !== action.meta.requestId) return;
        state.publicacaoAndamento = {
          requestId: null,
          publicando: false,
          erro: null,
          conflito: false,
          andamentoId: action.payload.andamentoId,
        };
      })
      .addCase(publicarAndamentoProcesso.rejected, (state, action) => {
        if (state.publicacaoAndamento.requestId !== action.meta.requestId) return;
        state.publicacaoAndamento = {
          requestId: null,
          publicando: false,
          erro: action.payload?.mensagem ?? action.error.message ?? 'Erro ao publicar o andamento',
          conflito: action.payload?.conflito ?? false,
          andamentoId: action.payload?.andamentoId ?? null,
        };
      })

      .addCase(fetchModelosAndamentoOpcoes.pending, (state) => {
        state.modelosAndamentoOpcoes.loading = true;
        state.modelosAndamentoOpcoes.error = null;
      })
      .addCase(fetchModelosAndamentoOpcoes.fulfilled, (state, action) => {
        state.modelosAndamentoOpcoes.loading = false;
        state.modelosAndamentoOpcoes.items = action.payload;
      })
      .addCase(fetchModelosAndamentoOpcoes.rejected, (state, action) => {
        state.modelosAndamentoOpcoes.loading = false;
        state.modelosAndamentoOpcoes.error = action.error.message ?? 'Erro ao carregar modelos';
      })

      .addCase(fetchDiasNaoUteis.pending, (state, action) => {
        state.diasNaoUteis.byAno[action.meta.arg] = { status: 'carregando' };
      })
      .addCase(fetchDiasNaoUteis.fulfilled, (state, action) => {
        state.diasNaoUteis.byAno[action.meta.arg] = { status: 'ok', calendario: action.payload };
      })
      .addCase(fetchDiasNaoUteis.rejected, (state, action) => {
        state.diasNaoUteis.byAno[action.meta.arg] = { status: 'erro' };
      })

      .addCase(fetchAuditoriaTriagem.pending, (state, action) => {
        const estado = estadoAuditoriaDoProcesso(state, action.meta.arg.processoId);
        estado.loading = true;
        estado.error = null;
      })
      .addCase(fetchAuditoriaTriagem.fulfilled, (state, action) => {
        const { processoId, page, anexar } = action.payload;
        const estado = estadoAuditoriaDoProcesso(state, processoId);
        const anterior = estado.page;
        estado.loading = false;
        estado.error = null;
        if (anexar && anterior) {
          const conhecidos = new Set(anterior.items.map((item) => item.id));
          estado.page = {
            ...page,
            items: [...anterior.items, ...page.items.filter((item) => !conhecidos.has(item.id))],
          };
        } else {
          estado.page = page;
        }
      })
      .addCase(fetchAuditoriaTriagem.rejected, (state, action) => {
        const estado = estadoAuditoriaDoProcesso(state, action.meta.arg.processoId);
        estado.loading = false;
        estado.error =
          action.payload ?? action.error.message ?? 'Erro ao carregar a auditoria da triagem';
      })

      .addCase(registrarExportacaoTriagem.pending, (state) => {
        state.exportacaoAuditoria.loading = true;
        state.exportacaoAuditoria.error = null;
      })
      .addCase(registrarExportacaoTriagem.fulfilled, (state, action) => {
        state.exportacaoAuditoria.loading = false;
        state.exportacaoAuditoria.error = null;
        invalidarAuditoriaTriagem(state, action.payload.processoIds);
      })
      .addCase(registrarExportacaoTriagem.rejected, (state, action) => {
        state.exportacaoAuditoria.loading = false;
        state.exportacaoAuditoria.error =
          action.payload ?? action.error.message ?? 'Erro ao registrar exportação na auditoria';
      })

      .addCase(fetchCertificadosProcesso.pending, (state) => {
        state.certificadosProcesso.loading = true;
        state.certificadosProcesso.error = null;
      })
      .addCase(fetchCertificadosProcesso.fulfilled, (state, action) => {
        state.certificadosProcesso.loading = false;
        state.certificadosProcesso.byProcessoId[action.payload.processoId] = action.payload.items;
      })
      .addCase(fetchCertificadosProcesso.rejected, (state, action) => {
        state.certificadosProcesso.loading = false;
        state.certificadosProcesso.error = action.error.message ?? 'Erro ao carregar certificados';
      })

      .addCase(fetchCertificadoAcessosProcesso.pending, (state, action) => {
        state.certificadoAcessosProcesso.loadingById[action.meta.arg] = true;
        state.certificadoAcessosProcesso.requestIdById[action.meta.arg] = action.meta.requestId;
        delete state.certificadoAcessosProcesso.errorById[action.meta.arg];
      })
      .addCase(fetchCertificadoAcessosProcesso.fulfilled, (state, action) => {
        if (
          state.certificadoAcessosProcesso.requestIdById[action.meta.arg] !== action.meta.requestId
        )
          return;
        delete state.certificadoAcessosProcesso.loadingById[action.meta.arg];
        state.certificadoAcessosProcesso.byProcessoId[action.payload.processoId] =
          action.payload.certificado;
        delete state.certificadoAcessosProcesso.errorById[action.meta.arg];
      })
      .addCase(fetchCertificadoAcessosProcesso.rejected, (state, action) => {
        if (
          state.certificadoAcessosProcesso.requestIdById[action.meta.arg] !== action.meta.requestId
        )
          return;
        delete state.certificadoAcessosProcesso.loadingById[action.meta.arg];
        state.certificadoAcessosProcesso.errorById[action.meta.arg] =
          action.error.message ?? 'Erro ao carregar o Certificado de Acessos';
      })

      .addCase(fetchChatProcesso.pending, (state) => {
        state.chatProcesso.loading = true;
        state.chatProcesso.error = null;
      })
      .addCase(fetchChatProcesso.fulfilled, (state, action) => {
        state.chatProcesso.loading = false;
        state.chatProcesso.byProcessoId[action.payload.processoId] = action.payload.page;
      })
      .addCase(fetchChatProcesso.rejected, (state, action) => {
        state.chatProcesso.loading = false;
        state.chatProcesso.error = action.error.message ?? 'Erro ao carregar chat';
      })

      .addCase(fetchAlertasAtivos.pending, (state, action) => {
        state.alertas.loading = true;
        state.alertas.error = null;
        state.alertas.requestId = action.meta.requestId;
      })
      // A busca acompanha a página de processos: a resposta de uma página que já saiu da tela
      // apagaria as marcas da página atual.
      .addCase(fetchAlertasAtivos.fulfilled, (state, action) => {
        if (state.alertas.requestId !== action.meta.requestId) return;
        state.alertas.loading = false;
        state.alertas.items = action.payload.items;
        state.alertas.totalGeral = action.payload.totalGeral;
        const byProcesso: Record<string, ArbitralAlertaItem[]> = {};
        for (const alerta of action.payload.items) {
          (byProcesso[alerta.processoArbitralId] ??= []).push(alerta);
        }
        state.alertas.byProcessoId = byProcesso;
      })
      .addCase(fetchAlertasAtivos.rejected, (state, action) => {
        if (state.alertas.requestId !== action.meta.requestId) return;
        state.alertas.loading = false;
        state.alertas.error = action.error.message ?? 'Erro ao carregar alertas';
      })

      .addCase(dispensarAlertasProcesso.fulfilled, (state, action) => {
        const { processoId, alertaIds } = action.payload;
        const dispensados = new Set(alertaIds);
        state.alertas.items = state.alertas.items.filter((a) => !dispensados.has(a.id));
        state.alertas.totalGeral = Math.max(0, state.alertas.totalGeral - dispensados.size);
        delete state.alertas.byProcessoId[processoId];
      });
  },
});

export const {
  setFilters,
  resetFilters,
  clearLastError,
  definirFiltroPendencias,
  descartarElegibilidadeTransito,
  adotarCasoTriagemAlvo,
  descartarElegiveisTriagem,
  limparCasoTriagemAlvo,
  resetDecisaoSolicitacao,
  resetConclusoSolicitacao,
  resetLote,
  resetPublicacaoAndamento,
} = slice.actions;
export const secretariaArbitralReducer = slice.reducer;
export default slice.reducer;
